import React, { useState, useCallback, useRef } from 'react';
import { withRouter, WithRouterProps } from 'react-router';
import I18n from 'i18n-js';
import { RCButton, RCTextWithIcon, buttonClassName } from '@reverbdotcom/commons/src/rc';
import { useCloseEventHandler } from '@reverbdotcom/commons/src/components/useCloseEventHandler';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import ChevronDownSvg from '@reverbdotcom/commons/design-system/icons/core/chevron-down.svg?component';
import ChevronUpSvg from '@reverbdotcom/commons/design-system/icons/core/chevron-up.svg?component';
import { Paths } from '../../../shared/url_helpers';
import { core_apimessages_Listing_SeedType as SeedType } from '../../../../schemas/rql';


interface IExternalProps {
  listingId?: string;
  dropdownId: string;
}

type IProps = IExternalProps & WithRouterProps;

const NAG_ID = 'sell_form_v2_opt_out';

function getRedirectPath({ location, listingId }) {
  if (listingId) {
    return Paths.legacySellFormEdit.expand({ id: listingId });
  }

  const {
    seed_id,
    seed_type = SeedType.ComparisonShoppingPage,
    promotional_token,
  } = location?.query ?? {};

  let params: any = {};
  if (promotional_token) {
    params.promotional_token = promotional_token;
  }

  if (seed_id) {
    params = { ...params, seed_id, seed_type };

    return Paths.legacyPrefilledSellForm.expand(params);
  }

  return Paths.legacySellFormBlank.expand({ ...params, blank: 'true' });
}

export function SellFormOptOut({ listingId, location, dropdownId }: IProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>();
  const menuTriggerId = `${dropdownId}-menu-trigger`;


  const closeHandler = useCallback(() => { setIsOpen(false); }, []);
  useCloseEventHandler({ isOpen, ref: dropdownRef, handler: closeHandler });

  const optOutLink = Paths.closeNagAlertAndRedirect.expand({
    nagId: NAG_ID,
    redirect_to: getRedirectPath({ location, listingId }),
  });

  return (
    <div className="sell-form-opt-out" ref={dropdownRef}>
      <RCButton
        id={menuTriggerId}
        variant="transparent"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="true"
        aria-controls={dropdownId}
        aria-expanded={isOpen}
      >
        <div className="sell-form-opt-out__trigger">
          <RCTextWithIcon svgComponent={isOpen ? ChevronUpSvg : ChevronDownSvg}>
            {I18n.t('discovery.sellForm.optOut.trigger')}
          </RCTextWithIcon>
        </div>
      </RCButton>
      {isOpen && (
        <div
          id={dropdownId}
          className="sell-form-opt-out__dropdown-container"
          aria-labelledby={menuTriggerId}
          tabIndex={-1}
        >
          <CoreLink
            to={optOutLink}
            className={buttonClassName({ variant: 'tertiary' })}
          >
            {I18n.t('discovery.sellForm.optOut.CTA')}
          </CoreLink>
          <div className="sell-form-opt-out__warning">
            {I18n.t('discovery.sellForm.optOut.warning')}
          </div>
        </div>
      )}
    </div>
  );
}

export default withRouter(SellFormOptOut);
